import { useState } from 'react'
import { router } from '@inertiajs/react'
import Modal from '../shared/Modal'
import { MetricCategory } from '../../types'

interface Props {
  isOpen: boolean
  onClose: () => void
  categories: MetricCategory[]
  teamId: number
}

export default function EditCategoriesModal({ isOpen, onClose, categories, teamId }: Props) {
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [saving, setSaving] = useState(false)

  const handleAdd = () => {
    if (!newName.trim()) return
    setSaving(true)
    router.post(
      `/teams/${teamId}/scoreboard/metric_categories`,
      { metric_category: { name: newName.trim(), position: categories.length } },
      {
        preserveScroll: true,
        onFinish: () => {
          setSaving(false)
          setNewName('')
        },
      }
    )
  }

  const handleUpdate = (id: number) => {
    if (!editingName.trim()) return
    setSaving(true)
    router.put(
      `/teams/${teamId}/scoreboard/metric_categories/${id}`,
      { metric_category: { name: editingName.trim() } },
      {
        preserveScroll: true,
        onFinish: () => {
          setSaving(false)
          setEditingId(null)
          setEditingName('')
        },
      }
    )
  }

  const handleDelete = (id: number, name: string) => {
    if (!confirm(`Delete category "${name}"? Metrics in this category will become uncategorized.`)) return
    router.delete(
      `/teams/${teamId}/scoreboard/metric_categories/${id}`,
      { preserveScroll: true }
    )
  }

  const startEditing = (cat: MetricCategory) => {
    setEditingId(cat.id)
    setEditingName(cat.name)
  }

  const cancelEditing = () => {
    setEditingId(null)
    setEditingName('')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Categories">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="New category name"
            className="flex-1 border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <button
            onClick={handleAdd}
            disabled={saving || !newName.trim()}
            className="px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red hover:bg-pinnacle-red/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            Add
          </button>
        </div>

        {categories.length === 0 ? (
          <p className="text-sm text-text-secondary text-center py-4">No categories yet. Add one above.</p>
        ) : (
          <div className="divide-y divide-stone/10">
            {categories.map(cat => (
              <div key={cat.id} className="flex items-center gap-2 py-2">
                {editingId === cat.id ? (
                  <>
                    <input
                      type="text"
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      className="flex-1 border border-stone/30 rounded-lg px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleUpdate(cat.id)
                        if (e.key === 'Escape') cancelEditing()
                      }}
                      autoFocus
                    />
                    <button
                      onClick={() => handleUpdate(cat.id)}
                      disabled={saving || !editingName.trim()}
                      className="p-1.5 text-status-on-track hover:bg-status-on-track/10 rounded transition-colors"
                      title="Save"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="p-1.5 text-text-muted hover:bg-stone/10 rounded transition-colors"
                      title="Cancel"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-text-primary">{cat.name}</span>
                    <button
                      onClick={() => startEditing(cat)}
                      className="p-1.5 text-text-muted hover:text-text-primary hover:bg-stone/10 rounded transition-colors"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(cat.id, cat.name)}
                      className="p-1.5 text-text-muted hover:text-[#C41920] hover:bg-[rgba(196,25,32,0.15)] rounded transition-colors"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-text-primary hover:bg-stone/10 rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  )
}
